import { notificationData } from "app/_interface/notifications";
import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

// converts "snake_case" to "Snake case", i.e capitalize
// the first letter and remove underscore 
export const formatTextFromSnakeCase = (text: string) => {
  if (!text) return '';

  return text
    .replace(/_/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Converts a object ID to the Base64 pattern
export function objectIdToPattern(objectId: string): string {
  // Convert hex string to a Buffer
  const buffer = Buffer.from(objectId, "hex")
  // Encode the buffer to Base64
  let base64 = buffer.toString("base64")
  // Convert to URL-safe Base64 by replacing '+' and '/' with URL-safe characters,
  // and remove any trailing '=' padding
  base64 = base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "")
  return base64
}

// Converts Base64 pattern to the original object ID
export function patternToObjectId(pattern: string): string {
  // Revert URL-safe Base64 to standard Base64 by reversing the character replacements
  let base64 = pattern.replace(/-/g, "+").replace(/_/g, "/")
  // Add back the padding if necessary so that the length is a multiple of 4
  while (base64.length % 4 !== 0) {
    base64 += "="
  }
  // Convert from Base64 back to Buffer, then to a hex string
  const buffer = Buffer.from(base64, "base64")
  return buffer.toString("hex")
}

/*
 * Combines multiple class names into a single string, removing duplicates
 * and resolving conflicts using Tailwind's `twMerge` utility.
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Function to get a cookie by name
export function getCookie(name: string) {
  const cookieValue = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${name}=`))
    ?.split("=")[1]

  return cookieValue
}

/**
 * Merges incoming notifications (single or array) with the current list,
 * deduplicates by _id (incoming notifications take precedence), and sorts
 * the result by timeStamp in descending order (latest first).
 *
 * @param current - The current array of notifications.
 * @param incoming - A single notification or an array of notifications to merge.
 * @returns The merged, deduplicated, and sorted array of notifications.
 */
export function mergeNotifications(
  current: notificationData[],
  incoming: notificationData | notificationData[]
): notificationData[] {
  // Normalize incoming to an array
  const incomingArray = Array.isArray(incoming) ? incoming : [incoming]

  // Merge and deduplicate by _id (latest wins)
  const map = new Map<string, notificationData>()
  // Add incoming first so they take precedence if IDs overlap
  for (const n of incomingArray) {
    map.set(n._id, n)
  }
  for (const n of current) {
    if (!map.has(n._id)) {
      map.set(n._id, n)
    }
  }

  // Sort by timeStamp descending (latest first)
  return Array.from(map.values()).sort((a, b) => new Date(b.timeStamp).getTime() - new Date(a.timeStamp).getTime())
}

function base64UrlEncode(arrayBuffer: ArrayBuffer | Uint8Array): string {
  let binary = '';
  const bytes = arrayBuffer instanceof Uint8Array ? arrayBuffer : new Uint8Array(arrayBuffer);
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]!);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export async function generateCodeVerifierAndChallenge(): Promise<{ code_verifier: string; code_challenge: string }> {
  const array = new Uint8Array(64);
  window.crypto.getRandomValues(array);
  const code_verifier = base64UrlEncode(array).substring(0, 128);

  const encoder = new TextEncoder();
  const data = encoder.encode(code_verifier);
  const digest = await window.crypto.subtle.digest("SHA-256", data);
  const code_challenge = base64UrlEncode(new Uint8Array(digest));

  return { code_verifier, code_challenge };
}